import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { ActiveNodeStyle } from "@/components/doc/right-sidebar/active-node-style/ActiveNodeStyle";
import { CodeBlockStyle } from "@/components/doc/right-sidebar/code-style/CodeBlockStyle";
import { CreateBlocksPanel } from "@/components/doc/right-sidebar/create-blocks/CreateBlocksPanel";
import { EmbedStyle } from "@/components/doc/right-sidebar/embed-style/EmbedStyle";
import { GraphStyle } from "@/components/doc/right-sidebar/graph-style/GraphStyle";
import { ImageStyle } from "@/components/doc/right-sidebar/image-style/ImageStyle";
import { LibrariesPanel } from "@/components/doc/right-sidebar/libraries/LibrariesPanel";
import { MarimoPackagesPanel } from "@/components/doc/right-sidebar/marimo-packages/MarimoPackagesPanel";
import { PlotlyChartStyle } from "@/components/doc/right-sidebar/plotly-chart-style/PlotlyChartStyle";
import { SymbolPalette } from "@/components/doc/right-sidebar/symbol-palette/SymbolPalette";
// import { SeparationLineStyle } from "@/components/doc/right-sidebar/separation-line-style/SeparationLineStyle";
import { TableCellStyle } from "@/components/doc/right-sidebar/table-style/TableCellStyle";
import { TableStyle } from "@/components/doc/right-sidebar/table-style/TableStyle";
import { TextStyle } from "@/components/doc/right-sidebar/text-style/TextStyle";
import { useStore } from "@/store";
import { useFeatureFlag } from "@/utils/useFeatureFlag";

import { DataAnalysisPanel } from "./data-analysis/DataAnalysisPanel";
import { GraphPreviewPanel } from "./graph-preview/GraphPreviewPanel";
import { SwitchNotations } from "./switch-notations/SwitchNotations";

import type { DocumentModel } from "@/models/DocumentModel";

export const TopPanel = observer(function TopPanel({ doc }: { doc: DocumentModel }) {
  const switchNotationsEnabled = useFeatureFlag("switch-notations");
  const coreLibrariesEnabled = useFeatureFlag("core-corca-libraries");
  const t = useTranslations("sidebar");
  const {
    view: { isRightSidebarCollapsed },
  } = useStore();

  const {
    selection: { hasSelectedNodes },
    editor: { editorState },
  } = doc;
  const {
    isText,
    isCodeBlock,
    isImage,
    isEmbed,
    isExpression,
    isTable,
    isTableCellLike,
    isGraph,
    isPlotlyChart,
    isCode,
  } = editorState;

  if (isRightSidebarCollapsed)
    return (
      <div className="flex flex-col gap-1">
        <CreateBlocksPanel doc={doc} />
        <ActiveNodeStyle doc={doc} />
        {!isTable && (isText || isExpression) && <TextStyle doc={doc} isCollapsed />}
        {(isTableCellLike || isTable) && (
          <div
            role="group"
            aria-label={t("toolbars.table")}
            className="flex flex-col gap-1 rounded-8 p-0.75 bg-gray-100 dark:bg-gray-900"
          >
            {isTableCellLike && <TableCellStyle doc={doc} isCollapsed />}
            {isTable && <TableStyle doc={doc} isCollapsed />}
          </div>
        )}
        {isCodeBlock && <CodeBlockStyle doc={doc} isCollapsed />}
        {isImage && <ImageStyle doc={doc} isCollapsed />}
        {isGraph && <GraphStyle doc={doc} isCollapsed />}
        {!hasSelectedNodes && !isCode && (
          <div className="flex flex-col gap-1 rounded-8 p-0.75 bg-gray-100 dark:bg-gray-900">
            <SymbolPalette doc={doc} isCollapsed />
          </div>
        )}
      </div>
    );

  return (
    <div className="flex flex-col">
      <CreateBlocksPanel doc={doc} />
      <GraphPreviewPanel doc={doc} />
      <DataAnalysisPanel doc={doc} />
      <ActiveNodeStyle doc={doc} />
      {!isTable && (isText || isExpression) && <TextStyle doc={doc} />}
      {isTableCellLike && <TableCellStyle doc={doc} />}
      {isTable && <TableStyle doc={doc} />}
      {isCodeBlock && <CodeBlockStyle doc={doc} />}
      {isCodeBlock && <MarimoPackagesPanel doc={doc} />}
      {isImage && <ImageStyle doc={doc} />}
      {isGraph && <GraphStyle doc={doc} />}
      {isPlotlyChart && <PlotlyChartStyle doc={doc} />}
      {/* TODO: isHorizontalRule && <SeparationLineStyle doc={doc} /> */}
      {isEmbed && <EmbedStyle doc={doc} />}
      {switchNotationsEnabled && isExpression && <SwitchNotations doc={doc} />}
      {coreLibrariesEnabled && <LibrariesPanel doc={doc} />}
      {!hasSelectedNodes && !isCode && <SymbolPalette doc={doc} />}
    </div>
  );
});
